import { Stack, Typography } from '@mui/material';
import { type ExposureOrGroup, useSubmission } from 'api/submission';
import { APPLICATION_STEPS, defaultBackendDateformat } from 'common/constants';
import { Modal } from 'components/Modal';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { QuestionEngine, useQuestionEngine } from 'components/lib/QuestionEngine';
import { formatDate } from 'helpers/utils';
import { isEmpty } from 'lodash-es';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { IClaim } from '../../Claims.types';
import type { ClaimModalProps, ClaimModalRef } from './ClaimModal.types';

type TGroup = ExposureOrGroup<IClaim>;

const { fields: initialFields } = APPLICATION_STEPS.claims;

const ClaimModal = forwardRef<ClaimModalRef | undefined, ClaimModalProps>(
  ({ handleAdd, handleUpdate, category }, ref) => {
    const [editingGroup, setEditingGroup] = useState<TGroup>({} as TGroup);
    const [open, setOpen] = useState(false);
    const isEditing = !isEmpty(editingGroup);
    const title = `${isEditing ? 'Update' : 'Add'} ${category}`;

    const { id } = useParams();
    const { data: submission } = useSubmission(String(id));

    const fields = useMemo(
      () =>
        initialFields.map((field) => {
          const isWCExcluded =
            submission?.data.uw_questions.numEmployees === '0' &&
            submission?.data?.groups?.pol_oo?.every(
              (pol_oo) => pol_oo.data.pol_oo_wc_excluded === 'Yes',
            );

          if (isWCExcluded && field.code === 'pol_claims_type') {
            return {
              ...field,
              choices: field.choices?.filter(
                (choice) => choice.code !== 'Workers Compensation Claim',
              ),
            };
          }

          return field;
        }),
      [submission?.data?.uw_questions?.numEmployees, submission?.data?.groups?.pol_oo],
    );

    const { validateFormik, ...questionEngineProps } = useQuestionEngine<IClaim>({ fields });
    const { formik, setState } = questionEngineProps;

    useImperativeHandle(ref, () => ({
      openModal: (group) => {
        if (group) {
          setEditingGroup(group);
          setState?.(group.data);
          formik?.setValues(group.data);
        }

        setOpen(true);
      },
    }));

    const resetModal = () => {
      setOpen(false);
      formik?.resetForm();
      setState?.({} as IClaim);
      setEditingGroup({} as TGroup);
    };

    const handleAction = async () => {
      const isValid = await validateFormik();

      if (!isValid) {
        return false;
      }

      const values: IClaim = {
        ...formik.values,
        pol_claims_date: formik.values.pol_claims_date
          ? formatDate(formik.values.pol_claims_date, defaultBackendDateformat)
          : '',
      };

      if (isEditing) {
        await handleUpdate(values, editingGroup?.id);
      } else {
        await handleAdd(values);
      }

      resetModal();
      return true;
    };

    return (
      <Modal
        open={open}
        title={title}
        onClose={resetModal}
        onCloseIconClick={() => resetModal()}
        modalContent={
          <Stack>
            <Typography variant="h2">Give details about claim</Typography>

            <QuestionEngine {...questionEngineProps} />
          </Stack>
        }
        modalFooter={
          <PrimaryButton text={title} fullWidth onClick={handleAction} data-test="add-claim" />
        }
      />
    );
  },
);

export default ClaimModal;
